import React, { useEffect, useState } from 'react';
import {
  faCheck,
  faFileLines,
  faTriangleExclamation,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Group, Paper, Stack, Text } from '../../../core';
import { plural } from '../../../../utils/plural';
import { surfaceHeaderProps } from '../SurfaceHeader';

/**
 * Written to `/results/<id>` by the host once staging resolves.
 *
 * There is no `applied` here and no premium: staging puts answers into a form
 * as drafts and saves nothing. A card that said "applied" would be describing
 * an account record that has not changed.
 */
export interface FormDraftResult {
  readonly status: 'staged' | 'rejected';
  readonly reason?: string;
  readonly formName?: string;
  readonly stagedCount?: number;
  readonly unchangedCount?: number;
  readonly unresolved?: readonly string[];
  readonly message?: string;
}

export interface FormDraftHandoffProps {
  readonly title: string;
  /** The questionnaire the answers were prepared for, so a mismatch is legible. */
  readonly formName?: string;
  readonly answerCount?: number;
  readonly result?: FormDraftResult;
  readonly pending?: boolean;
  /**
   * Fired ONCE on mount, by this card rather than by a click.
   *
   * The whole point of this flow is that the user does not press Apply in chat:
   * the answers land in the form and are reviewed there. But the change-set
   * lives in the action binding the agent supplies and the binder hands us an
   * opaque closure, so the card is the only thing that can fire it.
   */
  readonly onStage?: () => void;
  /**
   * Re-dispatch after a clearable refusal, for a caller that staged the answers
   * itself rather than through `onStage`.
   *
   * The deterministic path — the async-task card, staging straight from the
   * finished extraction — has already dispatched by the time this renders, so it
   * passes no `onStage` and the mount effect stays inert. Without a separate
   * prop the retry button would have to reuse `onStage`, which would make the
   * card fire a dispatch the caller had already made.
   */
  readonly onRetry?: () => void;
}

/**
 * Refusals the user can actually clear by navigating, as opposed to ones that
 * need a fresh request. Only these get a retry button — offering "Try again" on
 * a stale proposal would just refuse again.
 */
const RETRYABLE = new Set([
  'view-mismatch',
  'no-active-view',
  'busy',
  // The dispatch itself threw. That says nothing about whether the answers are
  // stageable, only that this attempt broke, so the user gets another go.
  'dispatch-failed',
]);

/** Shared with the hosts that stage answers themselves, so both halves of the
 * flow agree on which refusals are worth a retry button. */
export function isRetryableStageRefusal(reason?: string): boolean {
  return Boolean(reason && RETRYABLE.has(reason));
}

function StagedBody({ result }: Readonly<{ result: FormDraftResult }>) {
  // `undefined` is not zero. A hand-off replayed after a remount is told the
  // change-set was already staged, which is true and carries no count — and
  // "0 draft answers are on the questionnaire" reads as a failure.
  const count = result.stagedCount;
  return (
    <Stack gap={4}>
      <Group gap="xs" align="baseline">
        <FontAwesomeIcon
          icon={faCheck}
          color="var(--mantine-color-green-7)"
          size="sm"
        />
        <Text size="sm">
          {count === undefined ? (
            'Draft answers are on '
          ) : (
            <React.Fragment>
              <strong>{count}</strong> draft answer{plural(count)} are on{' '}
            </React.Fragment>
          )}
          {result.formName ?? 'the questionnaire'}. Review and accept them there
          — nothing has been saved to the account.
        </Text>
      </Group>
      {result.unchangedCount ? (
        <Text size="xs" c="dimmed">
          {result.unchangedCount} already matched what was on the form.
        </Text>
      ) : null}
      {result.unresolved && result.unresolved.length > 0 ? (
        <Text size="xs" c="dimmed">
          {result.unresolved.length} answer
          {plural(result.unresolved.length)} had no matching question and were
          left out.
        </Text>
      ) : null}
    </Stack>
  );
}

function RejectedBody({
  result,
  onRetry,
  disabled,
}: Readonly<{
  result: FormDraftResult;
  onRetry?: () => void;
  disabled: boolean;
}>) {
  return (
    <Stack gap="xs">
      <Group gap="xs" align="baseline">
        <FontAwesomeIcon
          icon={faTriangleExclamation}
          color="var(--mantine-color-yellow-8)"
          size="sm"
        />
        <Text size="sm">
          {result.message ?? 'Those answers could not be put on the form.'}
        </Text>
      </Group>
      {onRetry && isRetryableStageRefusal(result.reason) ? (
        <Box>
          <Button
            size="xs"
            variant="light"
            disabled={disabled}
            onClick={onRetry}
          >
            Try again
          </Button>
        </Box>
      ) : null}
    </Stack>
  );
}

/**
 * Extracted answers handed off to a form.
 *
 * Read-only by design — there is no Apply. The decision the user makes is on
 * the questionnaire itself, per answer, which is where they can see the
 * question next to the value.
 */
export function FormDraftHandoff({
  title,
  formName,
  answerCount,
  result,
  pending,
  onStage,
  onRetry,
}: FormDraftHandoffProps) {
  // State, not a ref: this latch has to survive the re-render its own dispatch
  // causes, and it is what makes a replayed card idempotent on the UI side.
  // The store dedupes by change-set id independently, because a mount effect is
  // not a durable guarantee.
  const [dispatched, setDispatched] = useState(false);

  useEffect(() => {
    if (!onStage || dispatched || result) {
      return;
    }
    setDispatched(true);
    onStage();
  }, [onStage, dispatched, result]);

  const waiting = !result;

  return (
    <Paper withBorder p="md" radius="md">
      <Stack gap="sm">
        <Group gap="xs" align="baseline" {...surfaceHeaderProps('md', 'none')}>
          <FontAwesomeIcon icon={faFileLines} size="sm" />
          {/* `sm`, matching the async-task card's own heading. This renders
              nested inside that card, and an `md` default read as a second,
              larger title competing with the run's. */}
          <Text size="sm" fw={600}>
            {title}
          </Text>
        </Group>

        {waiting ? (
          <Text size="sm" c="dimmed">
            Putting {answerCount ?? ''} answer
            {plural(answerCount)} on {formName ?? 'the questionnaire'}…
          </Text>
        ) : null}

        {result?.status === 'staged' ? <StagedBody result={result} /> : null}

        {result?.status === 'rejected' ? (
          <RejectedBody
            result={result}
            // `onStage` is the fallback: the mount latch is already set, so
            // re-firing it is exactly a retry.
            onRetry={onRetry ?? onStage}
            disabled={Boolean(pending)}
          />
        ) : null}
      </Stack>
    </Paper>
  );
}
